import { useState } from 'react';
import { Link } from 'react-router-dom';
import QueueTabContent from './components/QueueTabContent';
import GreetingsTabContent from './components/GreetingsTabContent';
import SongRequestsTabContent from './components/SongRequestsTabContent';
import ModerationTabContent from './components/ModerationTabContent';
import SettingsTabContent from './components/SettingsTabContent';

type CabinaTab = 'queue' | 'greetings' | 'songs' | 'moderation' | 'settings';

const cabinaTabs: { id: CabinaTab; label: string; icon: string }[] = [
  { id: 'queue', label: 'Queue', icon: 'ri-list-check' },
  { id: 'greetings', label: 'Saludos', icon: 'ri-user-heart-line' },
  { id: 'songs', label: 'Canciones', icon: 'ri-music-2-fill' },
  { id: 'moderation', label: 'Moderación', icon: 'ri-shield-line' },
  { id: 'settings', label: 'Config.', icon: 'ri-settings-3-line' },
];

function getTabAccent(tabId: CabinaTab): string {
  switch (tabId) {
    case 'queue': return 'bg-emerald-500';
    case 'greetings': return 'bg-emerald-500';
    case 'songs': return 'bg-accent-500';
    case 'moderation': return 'bg-red-500';
    case 'settings': return 'bg-secondary-500';
  }
}

export default function AdminCabinaPage() {
  const [activeTab, setActiveTab] = useState<CabinaTab>('queue');

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg ${getTabAccent(activeTab)} flex items-center justify-center`}>
              <i className={`${cabinaTabs.find((t) => t.id === activeTab)?.icon} text-background-50 text-xs`}></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Cabina</h1>
              <p className="text-foreground-400 text-[9px]">
                {cabinaTabs.find((t) => t.id === activeTab)?.label}
              </p>
            </div>
          </div>
          <Link to="/admin/dashboard" className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors cursor-pointer">
            <i className="ri-dashboard-line text-xs"></i>
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </div>

        {/* Mobile tab switcher */}
        <div className="flex items-center gap-1 px-3 pb-2 overflow-x-auto scrollbar-none">
          {cabinaTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const accent = getTabAccent(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                  isActive ? `${accent} text-background-50` : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className={`${tab.icon} text-[11px]`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex">
        <main className="flex-1 min-w-0 flex flex-col">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Dashboard
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Cabina — Centro de Operaciones</h2>
                <p className="text-foreground-400 text-xs">
                  Queue &bull; Saludos &bull; Canciones &bull; Moderación &bull; Configuración
                </p>
              </div>
            </div>

            {/* Desktop tab pills */}
            <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
              {cabinaTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                const accent = getTabAccent(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                      isActive ? `${accent} text-background-50 shadow-sm` : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${tab.icon} text-xs`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 flex flex-col min-h-0">
            {activeTab === 'queue' && <QueueTabContent />}
            {activeTab === 'greetings' && <GreetingsTabContent />}
            {activeTab === 'songs' && <SongRequestsTabContent />}
            {activeTab === 'moderation' && <ModerationTabContent />}
            {activeTab === 'settings' && <SettingsTabContent />}
          </div>
        </main>
      </div>


    </div>
  );
}